import { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  message,
} from "antd";
import dayjs from "dayjs";
import { createHost, updateHost } from "../../api/host";
import UserSelect from "../user/UserSelect";

/* ---------- 共享常量（列表页也会引用） ---------- */
export const CYCLE_MAP = {
  monthly: "月付",
  quarterly: "季付",
  yearly: "年付",
};
export const CYCLE_UNIT = {
  monthly: "月",
  quarterly: "季",
  yearly: "年",
}; // key 与 CYCLE_MAP 保持一致
const BALCONY_DIRECTIONS = [
  "东",
  "南",
  "西",
  "北",
  "东南",
  "西南",
  "东北",
  "西北",
  "无",
].map((v) => ({ value: v, label: v }));
const FLOOR_LEVELS = ["低", "中", "高"].map((v) => ({
  value: v,
  label: `${v}楼层`,
})); // 存的值不变，只改显示
export const STATUS_MAP = {
  empty: { label: "空置", color: "#64748b", bg: "#f1f5f9" },
  daily: { label: "日租", color: "#d48806", bg: "#fffbe6" },
  monthly: { label: "月租", color: "#1677ff", bg: "#e6f4ff" },
  quarterly: { label: "季租", color: "#722ed1", bg: "#f9f0ff" },
  yearly: { label: "年租", color: "#389e0d", bg: "#f6ffed" },
};
// TODO: 如与现有枚举不一致，按项目实际值修改
export const ROOM_TYPE_MAP = {
  single: "单人间",
  bedAndLiving: "一室一厅",
  twoBedRooms: "两室一厅",
  threeBedRooms: "三室两厅",
  duplex: "复式",
};
export const PREPAID_MAP = {
  notPay: { label: "未支付", color: "#cf1322", bg: "rgba(245,34,45,0.1)" },
  waitUse: { label: "未使用", color: "#d46b08", bg: "rgba(250,140,22,0.14)" },
  used: { label: "已使用", color: "#389e0d", bg: "rgba(82,196,26,0.12)" },
};

const toOptions = (map) =>
  Object.entries(map).map(([value, v]) => ({
    value,
    label: typeof v === "string" ? v : v.label,
  }));
const DATE_FIELDS = [
  "firstSigningDate",
  "contractExpiryDate",
  "rentValidDate",
  "propertyFeeExpiryDate",
  "garbageFeeExpiryDate",
];
const getId = (v) => (v && typeof v === "object" ? v._id : v);
// 多行输入框：框内第一行固定显示 label，用法同普通输入控件（交给 Form.Item 注入 value/onChange）
function NoteBox({ label, value, onChange, minRows = 2 }) {
  return (
    <div className="host-note">
      <div className="host-note-label">{label}</div>
      <Input.TextArea
        variant="borderless"
        autoSize={{ minRows, maxRows: 6 }}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}
/**
 * 房源新增/编辑抽屉（顶部抽屉）
 * props: open / record(null=新增) / currentUser / onClose / onSuccess
 */
function HostEditDrawerInner({
  open,
  record,
  currentUser,
  onClose,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isEdit = !!record?._id;

  const initialValues = useMemo(() => {
    if (!record) {
      return {
        status: "empty",
        prepaidWaterElectricityStatus: "used",
        valid: true,
        manager: currentUser?._id,
      };
    }
    const v = {
      ...record,
      owner: getId(record.owner),
      contactPerson: getId(record.contactPerson),
      manager: getId(record.manager),
    };
    DATE_FIELDS.forEach((k) => {
      v[k] = record[k] ? dayjs(record[k]) : null;
    });
    return v;
  }, [record, currentUser]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values };
      DATE_FIELDS.forEach((k) => {
        payload[k] = values[k] ? values[k].toISOString() : "";
      });
      setSaving(true);
      if (isEdit) await updateHost(record._id, payload);
      else await createHost(payload);
      message.success(isEdit ? "已保存" : "已新增");
      onSuccess?.();
    } catch (err) {
      if (err?.errorFields) return; // 表单校验未通过
      message.error(err?.response?.data?.message || err?.message || "保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      placement="top"
      height="92dvh"
      open={open}
      onClose={onClose}
      destroyOnClose
      title={isEdit ? `编辑房源 ${record.roomNumber || ""}` : "新增房源"}
      styles={{ body: { padding: "8px 16px 16px" } }}
      footer={
        <Space style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            保存
          </Button>
        </Space>
      }
    >
      {/* preserve={false}：关闭后清空字段值，避免上一次编辑的数据残留 */}
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        preserve={false}
        requiredMark={false}
      >
        <div className="host-form" style={{ maxWidth: 880, margin: "0 auto" }}>
          <style>{`
      .host-form .ant-select{width:100%}
      .host-form .ant-form-item{margin-bottom:12px}
      .host-note{border:1px solid #d9d9d9;border-radius:6px;padding:4px 11px 0;background:#fff}
      .host-note:focus-within{border-color:#4096ff;box-shadow:0 0 0 2px rgba(5,145,255,.1)}
      .host-note-label{font-size:12px;color:#8c8c8c}
      .host-note .ant-input{padding:0 0 4px}
    `}</style>

          <Divider orientation="left" plain style={{ margin: "4px 0 12px" }}>
            基本信息
          </Divider>
          <Row gutter={10}>
            <Col xs={12}>
              <Form.Item
                name="roomNumber"
                rules={[{ required: true, message: "请输入房号" }]}
              >
                <Input placeholder="房号" />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="roomType">
                <Select
                  allowClear
                  placeholder="户型"
                  options={toOptions(ROOM_TYPE_MAP)}
                />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="landParcel">
                <Input placeholder="地块号" />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="buildingName">
                <Input placeholder="楼栋" />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="floorNumber">
                <InputNumber
                  placeholder="楼层"
                  suffix="F"
                  style={{ width: "100%" }}
                  inputMode="numeric"
                />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="floorLevel">
                <Select
                  allowClear
                  placeholder="楼层级别"
                  options={FLOOR_LEVELS}
                />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="area">
                <InputNumber
                  min={0}
                  placeholder="面积"
                  suffix="㎡"
                  style={{ width: "100%" }}
                  inputMode="decimal"
                />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="balconyOrientation">
                <Select
                  allowClear
                  placeholder="主阳台方向"
                  prefix="阳台"
                  options={BALCONY_DIRECTIONS}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="valid"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox>有效房源</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            合同信息
          </Divider>
          <Row gutter={10}>
            <Col xs={8}>
              <Form.Item name="owner">
                <UserSelect placeholder="房东" />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="contactPerson">
                <UserSelect placeholder="对接人" />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="manager">
                <UserSelect allowCreate={false} placeholder="管理人" />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="firstSigningDate">
                <DatePicker
                  style={{ width: "100%" }}
                  inputReadOnly
                  placeholder="首次签约日期"
                  suffixIcon={
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>签约</span>
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="contractExpiryDate">
                <DatePicker
                  style={{ width: "100%" }}
                  inputReadOnly
                  placeholder="合同到期日期"
                  suffixIcon={
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>到期</span>
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="rentPaymentCycle">
                <Select
                  allowClear
                  placeholder="支付周期"
                  options={toOptions(CYCLE_MAP)}
                />
              </Form.Item>
            </Col>
            <Col xs={8}>
              {/* 倍率尾部的单位随支付周期变化 */}
              <Form.Item noStyle dependencies={["rentPaymentCycle"]}>
                {({ getFieldValue }) => (
                  <Form.Item name="rentPaymentCycleMultiplier">
                    <InputNumber
                      min={1}
                      placeholder="周期倍率"
                      suffix={CYCLE_UNIT[getFieldValue("rentPaymentCycle")]}
                      style={{ width: "100%" }}
                      inputMode="numeric"
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
            <Col xs={8}>
              <Form.Item name="rent">
                <InputNumber
                  min={0}
                  placeholder="租金"
                  prefix="¥"
                  style={{ width: "100%" }}
                  inputMode="decimal"
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="rentValidDate">
                <DatePicker
                  style={{ width: "100%" }}
                  inputReadOnly
                  placeholder="租金有效期"
                  prefix="房租"
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="prepaidWaterElectricityStatus">
                <Select
                  allowClear
                  placeholder="水电预存"
                  prefix="水电预存"
                  options={toOptions(PREPAID_MAP)}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="payToOwnerRecord">
                <NoteBox label="房租支付记录" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            市政费用
          </Divider>
          <Row gutter={10}>
            <Col xs={12}>
              <Form.Item name="propertyFeeExpiryDate">
                <DatePicker
                  style={{ width: "100%" }}
                  inputReadOnly
                  prefix="物业"
                  placeholder=""
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="garbageFeeExpiryDate">
                <DatePicker
                  style={{ width: "100%" }}
                  inputReadOnly
                  prefix="垃圾"
                  placeholder=""
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="utilityPayment">
                <NoteBox label="物业缴费说明" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "4px 0 12px" }} />
          <Form.Item name="description" style={{ marginBottom: 0 }}>
            <NoteBox label="备注" minRows={3} />
          </Form.Item>
        </div>
      </Form>
    </Drawer>
  );
}
export default function HostEditDrawer(props) {
  const [session, setSession] = useState(0);
  const [prevOpen, setPrevOpen] = useState(props.open);
  // 每次从关闭变为打开，换一个 key，抽屉和表单实例整体重建
  if (props.open !== prevOpen) {
    setPrevOpen(props.open);
    if (props.open) setSession((s) => s + 1);
  }
  return <HostEditDrawerInner key={session} {...props} />;
}
